/**
 * The three primitives the Predictions design is built from — card, section
 * header, list row — plus the avatar and the money formatter.
 *
 * Keeping them here rather than inlining per screen is what holds the handoff's
 * one hard rule: every content card is white with a hairline, and status is
 * typography rather than a coloured chip. A screen that needs a "highlight card"
 * has to come back and change this file, which is the conversation we want.
 */
import React from 'react';

export const Card = ({ children, list = false, className = '', ...rest }) => (
  <div
    className={`pr-card${list ? ' pr-card--list' : ''}${className ? ` ${className}` : ''}`}
    {...rest}
  >
    {children}
  </div>
);

export const SectionHeader = ({ label, meta, link, onLink, sub }) => (
  <div>
    <div className="pr-section">
      <span className="pr-section-label">{label}</span>
      {link ? (
        <button type="button" className="pr-section-link" onClick={onLink}>
          {link}
        </button>
      ) : meta ? (
        <span className="pr-section-meta">{meta}</span>
      ) : null}
    </div>
    {sub ? <div className="pr-section-sub">{sub}</div> : null}
  </div>
);

export const Row = ({ title, sub, value, onClick, children }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag type={onClick ? 'button' : undefined} className="pr-row" onClick={onClick}>
      {children}
      <span className="pr-row-main">
        <span className="pr-row-title">{title}</span>
        {sub ? <span className="pr-row-sub">{sub}</span> : null}
      </span>
      {value !== undefined && value !== null ? (
        <span className="pr-row-value">{value}</span>
      ) : null}
    </Tag>
  );
};

/** Initials, capped at two — the fallback when there is no member photo. */
export const initialsOf = (name) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

export const Avatar = ({ name, size = 44, me = false, photoUrl }) => (
  <span
    className={`pr-avatar${me ? ' pr-avatar--me' : ''}`}
    style={{ width: size, height: size, fontSize: Math.round(size * 0.34) }}
    aria-hidden="true"
  >
    {photoUrl ? <img src={photoUrl} alt="" /> : initialsOf(name)}
  </span>
);

/**
 * Money, always as ink. `signed` prefixes + or −, which the season standing
 * needs because there the sign IS the content.
 *
 * The minus is U+2212, not an ASCII hyphen: at these weights a hyphen reads as
 * a dash rather than a sign. (The PDF report hit the same thing for a different
 * reason — see `reportFormat.js`.)
 */
export const money = (amount, { signed = false, pence = false } = {}) => {
  const value = Number(amount) || 0;
  const abs = Math.abs(value);
  const body = pence || abs % 1 !== 0 ? abs.toFixed(2) : String(Math.round(abs));
  if (!signed) return `£${body}`;
  if (value === 0) return `£${body}`;
  return `${value > 0 ? '+' : '−'}£${body}`;
};

/** "nett" / "£10 nett" etc. — the handoff's terms line. */
export const FORMAT_LABELS = {
  stableford: 'Stableford',
  gross: 'Gross',
  nett: 'Nett',
  matchplay: 'Match',
};

export const KIND_LABELS = {
  h2h: 'Head to head',
  pairs: 'Pairs',
  sweep: 'Sweep',
};

export const Segmented = ({ options, value, onChange, inCard = false }) => (
  <div className={`pr-segmented${inCard ? ' pr-segmented--in-card' : ''}`}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        className={option.value === value ? 'is-selected' : ''}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);

export const Chips = ({ options, value, onChange, stake = false }) => (
  <div className="pr-chips">
    {options.map((option) => (
      <button
        key={String(option.value)}
        type="button"
        className={`pr-chip${stake ? ' pr-chip--stake' : ''}${
          option.value === value ? ' is-selected' : ''
        }`}
        onClick={() => onChange(option.value)}
      >
        {option.label}
      </button>
    ))}
  </div>
);
